import { useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { POIS, IMAGES } from "../../data/content";
import { BUSINESS, API } from "../../lib/api";
import { Reveal, SectionHeading } from "../Reveal";
import { Icon } from "../Icon";
import { MapPin, Download, Navigation } from "lucide-react";

export const Location = () => {
  const { t, lang } = useLang();
  const [selected, setSelected] = useState("pisa");
  const origin = encodeURIComponent("Via Capanne 13, 56020 Santa Maria a Monte PI, Italia");
  const dest = POIS.find((p) => p.key === selected) || POIS[0];
  const mapSrc = `https://www.google.com/maps?saddr=${origin}&daddr=${dest.lat},${dest.lng}&hl=${lang}&output=embed`;

  return (
    <section id="location" className="scroll-mt-20 bg-[#FAF8F5] px-4 sm:px-8 py-20 lg:py-28">
      <div className="mx-auto max-w-7xl">
        <Reveal>
          <SectionHeading eyebrow={t("location.eyebrow")} title={t("location.title")} subtitle={t("location.subtitle")} />
        </Reveal>

        <Reveal>
          <p className="mt-4 flex items-center gap-2 text-sm font-medium text-stone-500">
            <Navigation className="h-4 w-4 text-[#C85A32]" /> {t("location.routeHint")}
          </p>
        </Reveal>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="relative h-[420px] overflow-hidden rounded-2xl border border-stone-200 shadow-sm lg:h-full lg:min-h-[520px]">
              <iframe
                title="map"
                key={selected}
                data-testid="location-map"
                className="h-full w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                src={mapSrc}
              />
              <div className="pointer-events-none absolute bottom-3 left-3 z-[500] flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1.5 text-sm font-semibold text-stone-800 shadow">
                <MapPin className="h-4 w-4 text-[#C85A32]" /> {BUSINESS.addressArea}
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {POIS.map((p, i) => {
              const active = selected === p.key;
              return (
                <Reveal key={p.key} delay={Math.min(i * 0.05, 0.3)}>
                  <button
                    type="button"
                    onClick={() => setSelected(p.key)}
                    data-testid={`poi-${p.key}`}
                    aria-pressed={active}
                    className={`flex w-full items-center gap-3 rounded-2xl border bg-white p-4 text-left transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-0.5 hover:shadow-md ${
                      active ? "border-[#C85A32] ring-1 ring-[#C85A32]/30 shadow-md" : "border-stone-200/80"
                    }`}
                  >
                    <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors ${active ? "bg-[#C85A32]/12 text-[#C85A32]" : "bg-[#5F6B52]/12 text-[#5F6B52]"}`}>
                      <Icon name={p.icon} className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-stone-800">{t(`location.poi.${p.key}`)}</p>
                      <p className="text-xs text-stone-500">{p.m ? `${p.m} ${t("location.meters")}` : `${p.min} ${t(p.car ? "location.minutesCar" : "location.minutes")}`}</p>
                    </div>
                  </button>
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.15}>
          <div className="mt-8 flex justify-center">
            <a
              data-testid="download-visit-map"
              href={`${API}/visit-guide.pdf?lang=${lang}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 rounded-full bg-[#1C1917] px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#C85A32]"
            >
              <Download className="h-4 w-4" />
              {t("location.downloadMap")}
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
